import { ToastContext } from "context/ToastContext";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import {
  useCreateLineVocabularyMutation,
  useDeleteLineVocabularyMutation,
  useGetGlobalLineVocabularyQuery,
  useUpdateLineVocabularyMutation,
} from "store/queries/benefitLineVocabulary";
import styled from "styled-components";
import {
  IGlobalVocabularyView,
  IVocabularyRow,
  IVocabularySaveRequest,
} from "types/BenefitSelection/BenefitSelection";

import { LineTypeDialog } from "./LineTypeDialog";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

type LineTypeRow = IGlobalVocabularyView["rows"][number];

const keywordCell = (values: string[]) =>
  values.length === 0 ? (
    <span className="text-color-secondary">—</span>
  ) : (
    <div className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Tag key={value} value={value} className="surface-200 text-color" />
      ))}
    </div>
  );

/**
 * CLE-2792 — LINE TYPES: how each payer identifies a kind of benefit line.
 *
 * A line type ("Physician line") is what an account's selectors name; a DEFINITION says how one
 * payer, or every payer, identifies it. This is the ONE place they are edited — review round 1
 * removed the copy that lived on the payer edit page, because two editors for the same rows let a
 * reader see half the effective set and believe it was all of it.
 *
 * 🚨 EDITING A DEFINITION IS A MONEY DECISION FOR EVERY ACCOUNT WHOSE SELECTORS NAME THE TYPE, which
 * is why each row carries its "Referenced by" count and the confirm names the accounts. A payer's
 * own definition ADDS to the every-payer one; it never replaces it, so removing the every-payer row
 * does not fall back to a payer's — it removes wording the engine knew for every payer that has none.
 *
 * ⚠️ NO "TYPES WITH NO DEFINITION" BLOCK (review round 2). The hazard it listed — a selector naming
 * a type nobody has defined — is already reported where it can be acted on: in the selector editor
 * beside the type that has none, and in the explain panel for the benefit it declines. A third copy
 * on a page nobody visits to fix a selector was a list to read rather than a thing to do.
 */
export function LineTypesTab() {
  // `ToastContext` defaults to null, so read it defensively rather than destructuring it.
  const toast = useContext(ToastContext);
  const { data, isLoading, isError } = useGetGlobalLineVocabularyQuery();

  const [createRow, { isLoading: creating }] =
    useCreateLineVocabularyMutation();
  const [updateRow, { isLoading: updating }] =
    useUpdateLineVocabularyMutation();
  const [deleteRow] = useDeleteLineVocabularyMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IVocabularyRow | null>(null);

  const view = data?.data;

  const knownRoles = useMemo(() => {
    const roles = new Set<string>((view?.rows ?? []).map((row) => row.role));
    (view?.rolesInUseWithoutGlobalSpelling ?? []).forEach((gap) =>
      roles.add(gap.role)
    );
    return [...roles].sort();
  }, [view]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleSave = async (body: IVocabularySaveRequest) => {
    try {
      if (editing) {
        await updateRow({ id: editing.id, body }).unwrap();
      } else {
        await createRow(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Line type definition saved",
        detail: "New eligibility checks use it.",
      });
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Not saved",
        detail:
          error?.data?.message ||
          error?.message ||
          "The line type definition could not be saved.",
      });
    }
  };

  const confirmRemove = (row: LineTypeRow) =>
    confirmDialog({
      header: "Remove this line type definition?",
      style: { width: "min(520px, 95vw)" },
      // 🚨 THE ACCOUNTS ARE NAMED, not counted. "3 accounts" is a number to nod at; "Baystate,
      // Ascentist, Wagoner" is a list somebody recognises and stops on.
      message: `${row.role} is referenced by ${row.usedBy} account(s)${
        row.usedByAccounts.length
          ? `: ${row.usedByAccounts
              .map((account) => account.accountName ?? account.accountId)
              .join(", ")}`
          : ""
      }. Removing it means the payers it covers no longer identify ${
        row.role
      } at all, and the benefit is declined on those checks.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Remove",
      rejectLabel: "Keep it",
      accept: async () => {
        try {
          await deleteRow(row.id).unwrap();
        } catch (error: any) {
          toast?.current?.show({
            severity: "error",
            summary: "Not removed",
            detail: error?.data?.message || "The row could not be removed.",
          });
        }
      },
    });

  // 🚨 THE RAW VALUE IS THE NAME. A friendly-cased copy above it read as two things, and the one
  // people then said out loud was the one that matches no selector.
  const typeCellBody = (row: LineTypeRow) => (
    <span className="font-medium text-sm">{row.role}</span>
  );

  const payerCellBody = (row: LineTypeRow) => (
    <span className="text-sm" data-testid={`lineTypes.${row.id}.payer`}>
      {row.payerId === null
        ? "Every payer"
        : row.payerName ?? `Payer #${row.payerId}`}
    </span>
  );

  const matchCellBody = (row: LineTypeRow) => keywordCell(row.matchKeywords);

  const excludeCellBody = (row: LineTypeRow) =>
    keywordCell(row.excludeKeywords);

  const placeCellBody = (row: LineTypeRow) => (
    <span className="text-sm">{row.placeOfService ?? "any"}</span>
  );

  const reasonCellBody = (row: LineTypeRow) => (
    <span className="text-sm text-color-secondary">{row.reason}</span>
  );

  const usedByCellBody = (row: LineTypeRow) => (
    <span
      className="text-sm"
      data-testid={`lineTypes.${row.id}.usedBy`}
      title="Accounts whose line selections reference this type — editing or deleting a definition changes their estimates"
    >
      {row.usedBy} account{row.usedBy === 1 ? "" : "s"}
    </span>
  );

  const actionsCellBody = (row: LineTypeRow) => (
    <div className="flex gap-1">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${row.role}`}
        data-testid={`lineTypes.${row.id}.edit`}
        onClick={() => {
          setEditing(row);
          setDialogOpen(true);
        }}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${row.role}`}
        data-testid={`lineTypes.${row.id}.remove`}
        onClick={() => confirmRemove(row)}
      />
    </div>
  );

  return (
    <Surface data-testid="lineTypesTab">
      <div className="flex align-items-start justify-content-between gap-3 mb-3">
        <span className="text-sm text-color-secondary">
          A line type is what an account&apos;s selectors name. A definition
          says how one payer, or every payer, identifies it on a response.
        </span>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add definition"
          className="p-button-outlined p-button-sm"
          data-testid="lineTypes.add"
          onClick={() => openAdd()}
        />
      </div>

      {isError && (
        <StyledMessage
          severity="error"
          text="The line types could not be loaded."
        />
      )}

      <StyledDataTable
        value={view?.rows ?? []}
        dataKey="id"
        loading={isLoading}
        emptyMessage="No line type definitions yet. Until a type has one, the engine cannot identify it on any response."
        data-testid="lineTypes.table"
        className="mb-4"
      >
        <Column header="Line type" body={typeCellBody} />
        <Column header="Payer" body={payerCellBody} />
        <Column header="Contains" body={matchCellBody} />
        <Column header="Does not contain" body={excludeCellBody} />
        <Column header="Place of service" body={placeCellBody} />
        <Column header="Why" body={reasonCellBody} />
        <Column header="Referenced by" body={usedByCellBody} />
        <Column header="" style={{ width: "7rem" }} body={actionsCellBody} />
      </StyledDataTable>

      <LineTypeDialog
        visible={dialogOpen}
        row={editing}
        knownRoles={knownRoles}
        saving={creating || updating}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </Surface>
  );
}
